<script>
	import { planStore } from "./../store/plan-store";
	import ChatBubbleColorPicker from "./../ChatBubbleColorPicker.svelte";
	import ImageUploadCard from "./ImageUploadCard.svelte";
	import ImagePicker from "./ImagePicker.svelte";
	import { chatAssetFolder } from "./imageAssetApi";
	import { iframeRefStore } from "../store/iframe-store.js";
	import { PRConnect } from "@projectroom/ui-lib";
	import { Section, Switch, Textarea, Select } from "../../lib/ui";
	import { onMount, onDestroy, afterUpdate } from "svelte";

	export let chat;
	export let handleTextareaNextbreak;
	export let onImageFileUpload;

	let tab = "publish";
	let isCreepong;
	// 음성 채팅은 PRO 이상(ADMIN 포함) 요금제 전용 — 미만이면 토글을 잠그고 업셀 문구를 보여준다.
	// 요금제 정보(planStore)가 마운트 시점에 아직 안 들어와 있을 수 있어, 구독으로 값이 오면 갱신되게 한다.
	let isProPlan = false;
	const unsubPlan = planStore.subscribe(() => {
		isProPlan = planStore.isProOrAbove();
	});
	// Pro 전용 항목을 만지면 요금제 팝업(Plan.svelte)을 띄워 업그레이드로 유도한다.
	const openPlanPopup = () => planStore.setAuthSelectFlag(true);

	onMount(() => {
		isCreepong = planStore.isCreepong();
		isProPlan = planStore.isProOrAbove();
		initSortable();
		if (chat?.chatVersion === "V3") loadProfileLink();
	});

	onDestroy(() => unsubPlan());

	afterUpdate(() => {
		if (typeof jQuery === "undefined" || !jQuery.fn.sortable) return;
		const $list = jQuery(".fixed-button-list");
		if ($list.data("ui-sortable")) {
			$list.sortable("refresh");
		} else {
			initSortable();
		}
	});

	const initSortable = () => {
		if (typeof jQuery === "undefined" || !jQuery.fn.sortable) return;
		jQuery(".fixed-button-list").sortable({
			handle: ".move-icon",
			stop: () => {
				const orderedIds = [
					...document.querySelectorAll(".fixed-button-item"),
				].map((el) => el.dataset.id);
				chat.reorderFixedButtons(orderedIds);
			},
		});
	};

	// ── 영역 하이라이트: 설정 항목을 만지는 동안 미리보기에서 해당 자리를 깜빡여 알려준다 ──
	// (블록 탭의 프리뷰와 같은 postMessage 경로. 런타임 훅 useAreaHighlight가 받는다.)
	// ⚠️ V3(React 런타임)에서만 동작한다. V1/V2 미리보기는 정적 HTML이라 수신자가 없다.
	const AREA_HIGHLIGHT_MSG = "AVATALK_AREA_HIGHLIGHT";
	const AREA_HIGHLIGHT_CLEAR_MSG = "AVATALK_AREA_HIGHLIGHT_CLEAR";
	const postToIframe = (msg) => {
		const win = iframeRefStore.get()?.contentWindow;
		try {
			if (win) win.postMessage(msg, window.location.origin);
		} catch (e) {
			console.warn("[area-highlight:send] postMessage 실패", e);
		}
	};
	const highlightArea = (area) => postToIframe({ type: AREA_HIGHLIGHT_MSG, area });
	const clearHighlight = () => postToIframe({ type: AREA_HIGHLIGHT_CLEAR_MSG });
	// 탭 이탈 등으로 mouseleave를 못 받고 사라지는 경우 대비(미리보기에 깜빡임이 남지 않게).
	onDestroy(clearHighlight);

	// ── 상단 로고: 이미지 자산(에셋) 선택 — 블록 이미지와 같은 공용 ImagePicker 모달 사용 ──
	// 저장은 assetId(ref)만. 표시 URL은 서버가 current 버전으로 resolve해 내려준다(교체 시 자동 최신화).
	let logoPickerOpen = false;
	const onLogoPick = (e) => {
		const { assetId, url } = e.detail || {};
		chat.changeLogoAsset(assetId, url);
		chat = chat;
	};
	const onLogoRemove = () => {
		chat.changeLogoAsset(null, null);
		chat = chat;
	};

	// ── 프로필 페이지 연결 (채팅 위젯) ──────────────────────────────────────────
	// 고른 프로필 우측 하단에 채팅 아이콘이 뜬다. 저장은 BOTS.CHAT_ID.
	// 프로필 HTML을 다시 굽지 않는다 — 프로필 셸에 로더가 상시 박혀 있고 그 로더가 방문마다
	// 연결 여부를 조회하므로, 여기서 바꾸면 프로필 재발행 없이 다음 방문부터 반영된다.
	let profileList = [];
	let linkedBotId = "";
	let profileLoaded = false;

	const loadProfileLink = () => {
		PRConnect.send("/api/Chat/getChatProfileLink", { chatId: chat.chatId }, (data) => {
			if (data?.header?.status !== "success") return;
			profileList = data.result?.profiles ?? [];
			linkedBotId = data.result?.linkedBotId ?? "";
			profileLoaded = true;
		});
	};
	const onProfileChange = (e) => {
		const botId = e.target.value || "";
		PRConnect.send("/api/Chat/setChatProfileLink", { chatId: chat.chatId, botId }, (data) => {
			if (data?.header?.status !== "success") {
				alert(data?.header?.message || "프로필 연결에 실패했어요.");
				return;
			}
			linkedBotId = botId;
			loadProfileLink(); // 다른 프로필의 연결 상태(linkedByOther)도 갱신
		});
	};

	// 허용 도메인: 위젯을 띄울 수 있는 사이트 제한. 비우면 제한 없음.
	// 저장은 chat 필드로 → 기존 save() 경로(...this 스프레드)를 그대로 탄다.
	const onAllowedOriginsChange = (e) => chat.change("allowedOrigins", e.target.value);

	// 외부 사이트용 스니펫(프로필 연결과 별개 경로).
	// ⚠️ location.origin 을 쓰면 안 된다 — 로더는 S3에서 서빙된다(avatalk.in 은 S3를 /static/avatalk
	//    프리픽스로만 프록시하므로 avatalk.in/common/... 은 존재하지 않는 주소다).
	//    API 호출 주소는 위젯이 자체 DEFAULT_API_BASE 로 잡으므로 여기선 스크립트 위치만 맞으면 된다.
	const WIDGET_SRC =
		"https://groovworks-avatalk-public-file-storage.s3.ap-northeast-2.amazonaws.com/common/chat-widget/widget.js";
	$: widgetSnippet = `<script src="${WIDGET_SRC}" data-chat-id="${chat.chatId}" async><\/script>`;
	let snippetCopied = false;
	const copySnippet = async () => {
		try {
			await navigator.clipboard.writeText(widgetSnippet);
			snippetCopied = true;
			setTimeout(() => (snippetCopied = false), 1500);
		} catch {
			alert("복사에 실패했어요. 직접 선택해 복사해주세요.");
		}
	};

	// V3: 헤더 제목(챗 이름) 표시 토글. 로고와 독립 — 로고 옆에 제목을 같이 두거나, 로고만 둘 수 있다.
	// 저장 위치는 profileChatCss JSON(showProfileImage와 같은 자리) → 별도 컬럼/마이그레이션 불필요.
	let showTitle = chat?.profileChatCss?.showTitle !== false;
	const toggleShowTitle = () => {
		showTitle = !showTitle;
		if (chat?.profileChatCss) chat.profileChatCss.showTitle = showTitle;
		if (typeof chat?.save === "function") chat.save();
	};

	// V3 ⑨ 진행도 숫자 표기 — 상단 진행바와 [진행도] 패널이 같은 값을 쓴다.
	// 저장 위치도 showTitle과 같은 profileChatCss JSON → 컬럼/마이그레이션 불필요.
	// 미설정(기존 챗)은 'count' = 지금까지 진행바가 보여 온 표기 그대로.
	let progressFormat = chat?.profileChatCss?.progressFormat || "count";
	const onProgressFormatChange = (e) => {
		progressFormat = e.target.value;
		if (chat?.profileChatCss) chat.profileChatCss.progressFormat = progressFormat;
		if (typeof chat?.save === "function") chat.save();
	};

	const onChangeCharacterMessage = (e) =>
		chat.changeCharacterMessage(e.target.value);
	const onChangeAdditionalMessage = (e) =>
		chat.changeAdditionalMessage(e.target.value);
	const clickCategory = (e) => {
		const categoryElement = e.target.closest(".category");
		chat.changeCategory(categoryElement.dataset.no);
	};
	const toggleRandomMessageFlag = () => chat.changeRandomMessageFlag();
	const toggleAdditionalMessageFlag = () => chat.changeAdditionalMessageFlag();
	const onFixedButtonTitleChange = (id, e) =>
		chat.updateFixedButton(id, "title", e.target.value);
	const onFixedButtonUrlChange = (id, e) =>
		chat.updateFixedButton(id, "url", e.target.value);
	const handleCharacterMessage = (e) =>
		handleTextareaNextbreak(e, "characterMessage");
	const handleAdditionalMessage = (e) =>
		handleTextareaNextbreak(e, "additionalMessage");

	// 읽어주기 "말투" — OpenAI TTS 의 instructions(연기 지시)로 그대로 나간다.
	// 프리셋을 고르면 아래 칸이 그 문장으로 채워지고, 저작자가 직접 다듬을 수 있다.
	// ⚠️ 지시로 바뀌는 건 톤·속도·억양까지다. 화자의 음색 자체는 안 바뀐다 —
	//    OpenAI TTS 보이스(alloy/nova/coral…)는 전부 성인 화자 녹음이라 "어린이 목소리"는 흉내에 가깝다.
	//    (진짜 아동 화자가 필요하면 Polly 아동 보이스(영어 전용)나 외부 제공사 연동이 따로 필요하다.)
	const TTS_TONE_PRESETS = [
		{
			id: "child",
			label: "어린이처럼 (밝고 높은 톤)",
			text: "초등학생 아이처럼 밝고 높은 톤으로 읽어주세요. 또박또박, 조금 들뜬 느낌으로 문장 끝을 살짝 올려주세요.",
		},
		{
			id: "teen",
			label: "10대처럼 (친구 같은 말투)",
			text: "10대 청소년이 친구에게 말하듯 가볍고 편안한 톤으로, 밝고 빠르게 읽어주세요.",
		},
		{
			id: "warm",
			label: "다정한 선생님",
			text: "따뜻하고 다정한 선생님처럼 천천히 또박또박 읽어주세요. 칭찬하는 부분은 진심이 느껴지게 힘을 실어주세요.",
		},
		{
			id: "cheerful",
			label: "활기찬 진행자",
			text: "에너지 있고 활기찬 톤으로 리듬감 있게 읽어주세요. 너무 빠르지 않게, 또렷하게.",
		},
		{
			id: "calm",
			label: "차분한 안내",
			text: "낮고 차분한 톤으로 침착하게, 신뢰감 있게 읽어주세요.",
		},
	];
	// 저장된 지시문이 프리셋 문장과 정확히 같으면 그 프리셋, 손대서 달라졌으면 '직접 입력'으로 본다.
	$: tonePresetId = !chat.ttsInstructions
		? ""
		: (TTS_TONE_PRESETS.find((p) => p.text === chat.ttsInstructions)?.id ?? "custom");
	const onTonePresetChange = (e) => {
		const id = e.target.value;
		// 'custom'은 이미 직접 입력한 상태에서만 목록에 뜨는 표시용 항목 — 고른다고 문장을 덮어쓰지 않는다.
		if (id === "custom") return;
		chat.change("ttsInstructions", TTS_TONE_PRESETS.find((p) => p.id === id)?.text ?? "");
	};
	const onToneTextChange = (e) => chat.change("ttsInstructions", (e.target.value ?? "").trim());

	const inputCls =
		"tw-flex tw-h-10 tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-text-foreground placeholder:tw-text-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring";
</script>

{#if chat.isCurrentTab(tab)}
	<div class="tw-space-y-4 tw-font-sans tw-text-foreground">
		<!-- 첫/덧붙임 메시지·고정버튼은 블록 모드(V3)에선 숨김 -->
		{#if chat.chatVersion !== "V3"}
			<Section title="AI챗이 보내는 첫 메시지를 작성해주세요!">
				<svelte:fragment slot="badge">
					<div class="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
						<span class="tw-text-xs tw-text-muted-foreground">첫 메시지 고정</span>
						<Switch checked={chat.isRandomMessageFlag()} on:click={toggleRandomMessageFlag} />
					</div>
				</svelte:fragment>
				{#if chat.isRandomMessageFlag()}
					<Textarea
						rows={chat.getCharacterMessageRows()}
						value={chat.characterMessage}
						placeholder="AI챗이 보내는 고정 첫 메시지를 작성해보세요!"
						on:change={onChangeCharacterMessage}
						on:input={handleCharacterMessage}
					/>
				{:else}
					<p class="tw-text-sm tw-text-muted-foreground">'첫 메시지 고정'을 켜면 고정 첫 메시지를 입력할 수 있어요.</p>
				{/if}
			</Section>

			<Section title="AI챗이 보내는 덧붙임 메시지를 작성해주세요!">
				<svelte:fragment slot="badge">
					<Switch checked={chat.isAdditionalMessageFlag()} on:click={toggleAdditionalMessageFlag} />
				</svelte:fragment>
				{#if chat.isAdditionalMessageFlag()}
					<Textarea
						rows={chat.getAdditionalMessageRows()}
						value={chat.additionalMessage}
						placeholder="AI챗 마지막 답변에 추가될 고정 멘트를 설정해보세요!"
						on:change={onChangeAdditionalMessage}
						on:input={handleAdditionalMessage}
					/>
				{:else}
					<p class="tw-text-sm tw-text-muted-foreground">토글을 켜면 덧붙임 메시지를 입력할 수 있어요.</p>
				{/if}
			</Section>

			<Section title="하단 고정 버튼을 추가해주세요!">
				<svelte:fragment slot="badge">
					<Switch checked={chat.isUseFixedButton()} on:click={() => chat.toggleUseFixedButton()} />
				</svelte:fragment>
				{#if chat.isUseFixedButton()}
					<ul class="fixed-button-list">
						{#each chat.fixedButtonList as btn, i (btn.id)}
							<li class="fixed-button-item" data-id={btn.id}>
								<img
									width="15"
									loading="lazy"
									src="/static/images/move-icon.svg"
									alt=""
									class="move-icon"
								/>
								<div class="tw-flex-1 tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-p-3">
									<div class="tw-mb-2 tw-flex tw-items-center tw-justify-between">
										<span class="tw-text-sm tw-font-medium tw-text-foreground">버튼 {i + 1}</span>
										<button
											type="button"
											class="tw-inline-flex tw-h-7 tw-w-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground hover:tw-bg-destructive/10 hover:tw-text-destructive"
											aria-label="삭제"
											on:click={() => chat.deleteFixedButton(btn.id)}
										>
											<svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>
										</button>
									</div>
									<div class="tw-space-y-2">
										<input
											type="text"
											class={inputCls}
											maxlength="256"
											placeholder="버튼 문구를 입력해주세요!"
											value={btn.title}
											on:blur={(e) => onFixedButtonTitleChange(btn.id, e)}
											on:change={(e) => onFixedButtonTitleChange(btn.id, e)}
										/>
										<input
											type="text"
											class={inputCls}
											maxlength="512"
											placeholder="연결할 링크 주소를 붙여주세요!"
											value={btn.url}
											on:blur={(e) => onFixedButtonUrlChange(btn.id, e)}
											on:change={(e) => onFixedButtonUrlChange(btn.id, e)}
										/>
									</div>
								</div>
							</li>
						{/each}
					</ul>
					<button
						type="button"
						class="tw-inline-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-dashed tw-border-border tw-px-4 tw-text-sm tw-font-medium tw-text-primary hover:tw-bg-primary/5"
						on:click={() => chat.addFixedButton()}
					>+ 버튼 추가하기</button>
				{:else}
					<p class="tw-text-sm tw-text-muted-foreground">토글을 켜면 하단 고정 버튼을 추가할 수 있어요.</p>
				{/if}
			</Section>
		{/if}

		{#if chat.chatVersion === "V3"}
			<!-- 프로필 페이지 연결 / 외부 사이트 임베드 -->
			<Section title="채팅 위젯을 붙일 곳을 골라주세요!">
				<div class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">프로필 페이지</div>
				{#if profileLoaded && profileList.length === 0}
					<p class="tw-text-sm tw-text-muted-foreground">
						연결할 수 있는 프로필 페이지가 없어요. 엔터프라이즈 프로필 페이지를 먼저 만들어주세요.
					</p>
				{:else}
					<select
						class="tw-h-10 tw-w-full tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm"
						value={linkedBotId}
						on:change={onProfileChange}
					>
						<option value="">연결 안 함</option>
						{#each profileList as p (p.botId)}
							<option value={p.botId}>
								{p.title}{p.linkedByOther ? " (다른 챗에 연결됨)" : ""}
							</option>
						{/each}
					</select>
					<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
						고른 프로필 페이지 우측 하단에 채팅 아이콘이 생겨요. 프로필을 다시 발행하지 않아도 바로 반영됩니다.
					</p>
				{/if}

				<div class="tw-mt-4 tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">다른 웹사이트에 붙이기</div>
				<div class="tw-flex tw-gap-2">
					<input
						type="text"
						class="tw-h-10 tw-flex-1 tw-rounded-md tw-border tw-border-border tw-bg-muted/40 tw-px-3 tw-text-xs tw-text-muted-foreground"
						readonly
						value={widgetSnippet}
						on:focus={(e) => e.target.select()}
					/>
					<button
						type="button"
						class="tw-h-10 tw-shrink-0 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-px-4 tw-text-sm tw-font-medium hover:tw-bg-muted"
						on:click={copySnippet}
					>{snippetCopied ? "복사됨" : "복사"}</button>
				</div>
				<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
					이 한 줄을 웹사이트 &lt;/body&gt; 앞에 넣으면 채팅 아이콘이 생겨요.
				</p>

				<div class="tw-mt-4 tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">허용 도메인</div>
				<Textarea
					value={chat.allowedOrigins ?? ""}
					rows="3"
					placeholder={"https://example.com\n*.example.com"}
					on:change={onAllowedOriginsChange}
					on:blur={onAllowedOriginsChange}
				/>
				<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
					한 줄에 하나씩. 비워두면 어느 사이트에서든 붙일 수 있어요. 스니펫이 유출됐을 때 남의 사이트에서
					쓰이는 걸 막습니다.
				</p>
			</Section>
		{/if}

		<!-- 음성 채팅 (V3 런타임 전용) — PRO 이상 요금제에서만 켤 수 있다 -->
		{#if chat.chatVersion === "V3"}
			<Section title="음성 채팅">
				<svelte:fragment slot="badge">
					{#if !isProPlan}
						<span class="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-bg-muted tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-muted-foreground">
							<img src="/static/images/lock.svg" alt="" class="tw-h-3 tw-w-3" />
							PRO 이상
						</span>
					{/if}
				</svelte:fragment>
				<div class="tw-space-y-3">
					<div
						class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2.5"
						class:tw-opacity-60={!isProPlan}
						class:tw-cursor-pointer={!isProPlan}
						on:click={() => !isProPlan && openPlanPopup()}
					>
						<div>
							<p class="tw-text-sm tw-font-medium tw-text-foreground">읽어주기 (TTS)</p>
							<p class="tw-text-xs tw-text-muted-foreground">AI챗의 답변을 음성으로 읽어줘요.</p>
						</div>
						<Switch
							checked={isProPlan && chat.isTtsFlag()}
							on:click={() => (isProPlan ? chat.changeTtsFlag() : openPlanPopup())}
						/>
					</div>
					<!-- 읽어주기 목소리(OpenAI tts-1 보이스). TTS 켜짐 + PRO 이상일 때만 선택 가능.
					     엔진/모델은 기본 OpenAI tts-1로 고정하고 저작자에겐 목소리만 고르게 한다. -->
					<div
						class="tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2.5"
						class:tw-opacity-60={!isProPlan || !chat.isTtsFlag()}
					>
						<p class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">목소리</p>
						<Select
							value={chat.ttsVoice}
							disabled={!isProPlan || !chat.isTtsFlag()}
							on:change={(e) => chat.changeTtsVoice(e.target.value)}
						>
							<option value="">기본 (Alloy · 중립)</option>
							<option value="nova">Nova · 여성 (밝음)</option>
							<option value="coral">Coral · 여성 (밝고 어린 느낌)</option>
							<option value="shimmer">Shimmer · 여성 (부드러움)</option>
							<option value="echo">Echo · 남성</option>
							<option value="onyx">Onyx · 남성 (저음)</option>
							<option value="fable">Fable · 남성 (영국식·스토리텔링)</option>
						</Select>
						<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
							AI챗이 답변을 읽어줄 목소리예요. 같은 문장·목소리는 캐시돼 한 번만 과금됩니다.
						</p>
					</div>
					<!-- 말투(연기 지시). 값이 있으면 서버가 지시를 받을 수 있는 모델(gpt-4o-mini-tts)로 자동 전환한다.
					     저작자는 모델을 몰라도 되고, 톤만 고르면 된다. -->
					<div
						class="tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2.5"
						class:tw-opacity-60={!isProPlan || !chat.isTtsFlag()}
					>
						<p class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">말투</p>
						<Select
							value={tonePresetId}
							disabled={!isProPlan || !chat.isTtsFlag()}
							on:change={onTonePresetChange}
						>
							<option value="">기본 (지시 없음)</option>
							{#each TTS_TONE_PRESETS as preset}
								<option value={preset.id}>{preset.label}</option>
							{/each}
							{#if tonePresetId === "custom"}
								<option value="custom">직접 입력한 말투</option>
							{/if}
						</Select>
						<div class="tw-mt-2">
							<Textarea
								rows="3"
								value={chat.ttsInstructions ?? ""}
								disabled={!isProPlan || !chat.isTtsFlag()}
								maxlength="500"
								placeholder="예: 초등학생 아이처럼 밝고 높은 톤으로 또박또박 읽어주세요."
								on:change={onToneTextChange}
								on:blur={onToneTextChange}
							/>
						</div>
						<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
							읽어줄 때의 톤·속도·억양을 지시해요. 프리셋을 고른 뒤 문장을 직접 다듬어도 됩니다.
							어린 느낌은 목소리 <b>Coral·Nova·Shimmer</b>와 함께 쓸 때 가장 잘 나와요.
						</p>
						<p class="tw-mt-1 tw-text-xs tw-text-muted-foreground">
							⚠️ 목소리의 <b>음색 자체</b>가 아이로 바뀌지는 않아요. 지금 쓰는 음성 엔진의 목소리는 모두
							성인 화자라, 말투 지시로 "어리게 들리도록" 연기시키는 방식입니다.
						</p>
					</div>
					<div
						class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2.5"
						class:tw-opacity-60={!isProPlan}
						class:tw-cursor-pointer={!isProPlan}
						on:click={() => !isProPlan && openPlanPopup()}
					>
						<div>
							<p class="tw-text-sm tw-font-medium tw-text-foreground">음성 입력 (STT)</p>
							<p class="tw-text-xs tw-text-muted-foreground">사용자가 마이크로 말해서 입력할 수 있어요.</p>
						</div>
						<Switch
							checked={isProPlan && chat.isSttFlag()}
							on:click={() => (isProPlan ? chat.changeSttFlag() : openPlanPopup())}
						/>
					</div>
					<!-- 음성 전용 입력. 텍스트 입력창을 없애 "말해서만" 입력하게 한다(말하기 연습 챗용).
					     STT가 꺼져 있으면 켤 수 없다 — 켜지면 입력 수단이 하나도 없는 챗이 되기 때문. -->
					<div
						class="tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2.5"
						class:tw-opacity-60={!isProPlan || !chat.isSttFlag()}
					>
						<div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
							<div>
								<p class="tw-text-sm tw-font-medium tw-text-foreground">음성으로만 입력받기</p>
								<p class="tw-text-xs tw-text-muted-foreground">
									키보드 입력창을 없애고 말해서만 답하게 해요.
								</p>
							</div>
							<Switch
								checked={isProPlan && chat.isSttFlag() && chat.isSttOnly()}
								disabled={!isProPlan || !chat.isSttFlag()}
								on:click={() => {
									if (!isProPlan) return openPlanPopup();
									if (chat.isSttFlag()) chat.changeSttOnly();
								}}
							/>
						</div>
						<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
							말한 내용을 받아쓴 뒤 <b>다시 말하기 / 전송</b>을 고르게 됩니다.
							말하기 연습에는 좋지만, <b>마이크를 쓸 수 없는 사용자는 이 채팅을 이용할 수 없습니다</b>
							— 권한을 막았거나, 마이크가 없거나, 지원하지 않는 브라우저면 안내만 보게 돼요.
							(설문·상담처럼 아무나 들어오는 채팅에는 권하지 않습니다)
							인식이 계속 실패하면 그 차례만 키보드가 열려요 — 갇히지 않게 하는 안전장치입니다.
						</p>
						<!-- 받아쓴 문장 수정 허용. 강제를 입력 수단이 아니라 기록으로 옮기는 스위치다 —
						     켜 두어도 손댄 발화는 CHAT_LOG.INPUT_MODE에 'voice-edited'로 남는다. -->
						<div
							class="tw-mt-2.5 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-border tw-pt-2.5"
							class:tw-opacity-60={!isProPlan || !chat.isSttFlag() || !chat.isSttOnly()}
						>
							<div>
								<p class="tw-text-sm tw-font-medium tw-text-foreground">받아쓴 문장 고치기 허용</p>
								<p class="tw-text-xs tw-text-muted-foreground">
									받아쓴 문장 옆 <b>[고치기]</b>로 잘못 인식된 부분을 키보드로 바로잡을 수 있어요.
									학습자는 이미 소리 내어 말한 뒤라 <b>말하기 연습을 건너뛰는 게 아니고</b>,
									손을 댄 발화는 기록에 따로 표시되어 저작자가 구분할 수 있습니다.
									끄면 잘못 나왔을 때 <b>[다시 말하기]</b>로 처음부터 다시 말해야 해요(시험처럼 엄격한 챗용).
								</p>
							</div>
							<Switch
								checked={isProPlan && chat.isSttFlag() && chat.isSttOnly() && chat.isSttOnlyEdit()}
								disabled={!isProPlan || !chat.isSttFlag() || !chat.isSttOnly()}
								on:click={() => {
									if (!isProPlan) return openPlanPopup();
									if (chat.isSttFlag() && chat.isSttOnly()) chat.changeSttOnlyEdit();
								}}
							/>
						</div>
						<!-- 적용 구간. 수업(스텝)은 말하기로 시키고 질문은 타이핑하게 두는 저작을 위해 나눈 값.
						     런타임은 진행바와 같은 신호(스텝 응답에만 실리는 step 메타)로 구간을 판정한다. -->
						<div class="tw-mt-2.5" class:tw-opacity-60={!isProPlan || !chat.isSttFlag() || !chat.isSttOnly()}>
							<p class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">적용 구간</p>
							<Select
								value={chat.sttOnlyScope}
								disabled={!isProPlan || !chat.isSttFlag() || !chat.isSttOnly()}
								on:change={(e) => chat.changeSttOnlyScope(e.target.value)}
							>
								<option value="all">항상 (채팅 전체)</option>
								<option value="step">수업(스텝) 진행 중에만</option>
								<option value="free">자유 대화에서만</option>
							</Select>
							<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
								<b>수업(스텝) 진행 중에만</b>으로 두면 레슨·평가를 진행하는 동안에는 말해서만 답하고,
								수업 밖 자유 대화에서는 키보드 입력창과 마이크가 함께 나옵니다
								(말하기는 수업에서 연습시키고, 질문은 편하게 타이핑하게 두는 구성).
							</p>
						</div>
						<!-- 핸즈프리. 자동으로 '열어 주는' 것뿐이고 막는 게 아니다 — 꺼도 [눌러서 말하기]는 그대로다.
						     읽어주기(TTS)가 있어야 "다 읽은 시점"이 생기므로 TTS가 꺼져 있으면 고를 수 없다. -->
						<div
							class="tw-mt-2.5 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-border tw-pt-2.5"
							class:tw-opacity-60={!isProPlan || !chat.isSttFlag() || !chat.isSttOnly() || !chat.isTtsFlag()}
						>
							<div>
								<p class="tw-text-sm tw-font-medium tw-text-foreground">다 읽어주면 마이크 자동 열기</p>
								<p class="tw-text-xs tw-text-muted-foreground">
									봇이 답변을 끝까지 읽은 뒤 마이크가 저절로 열려요(듣고 바로 대답하는 흐름).
									꺼 두면 사용자가 <b>[눌러서 말하기]</b>를 직접 누릅니다. 켜 두더라도 다 듣기 전에
									직접 눌러 말할 수 있어요 — 막지는 않습니다.
								</p>
							</div>
							<Switch
								checked={isProPlan && chat.isSttFlag() && chat.isSttOnly() && chat.isTtsFlag() && chat.isVoiceHandsfree()}
								disabled={!isProPlan || !chat.isSttFlag() || !chat.isSttOnly() || !chat.isTtsFlag()}
								on:click={() => {
									if (!isProPlan) return openPlanPopup();
									if (chat.isSttFlag() && chat.isSttOnly() && chat.isTtsFlag()) chat.changeVoiceHandsfree();
								}}
							/>
						</div>
					</div>
					<!-- 말 기다리는 시간(녹음 자동 종료). 음성 전용이든 아니든 모든 녹음에 걸린다.
					     '기본'(값 없음)의 4초·1.5초는 CHAT useRecorder 의 기본 상수와 같아야 한다(바꾸면 함께).
					     선택지 밖의 값(SQL 등으로 넣은 값)도 지워지지 않게 그 값을 선택지로 하나 더 보인다. -->
					<div
						class="tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2.5"
						class:tw-opacity-60={!isProPlan || !chat.isSttFlag()}
					>
						<p class="tw-text-sm tw-font-medium tw-text-foreground">말 기다리는 시간</p>
						<p class="tw-mb-2 tw-text-xs tw-text-muted-foreground">
							마이크가 알아서 녹음을 끝내는 시점이에요. 어린이처럼 생각하면서 말하는 학습자라면 둘 다 늘려 주세요.
						</p>
						<p class="tw-mb-1.5 tw-text-xs tw-font-medium tw-text-foreground">첫마디 기다리기</p>
						<Select
							value={chat.sttStartWaitMs ? String(chat.sttStartWaitMs) : ""}
							disabled={!isProPlan || !chat.isSttFlag()}
							on:change={(e) => chat.changeSttStartWaitMs(e.target.value)}
						>
							<option value="">기본 (4초)</option>
							<option value="7000">7초</option>
							<option value="10000">10초</option>
							<option value="15000">15초</option>
							<option value="20000">20초</option>
							{#if chat.sttStartWaitMs && ![7000, 10000, 15000, 20000].includes(chat.sttStartWaitMs)}
								<option value={String(chat.sttStartWaitMs)}>{chat.sttStartWaitMs / 1000}초</option>
							{/if}
						</Select>
						<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
							마이크가 열린 뒤 이 시간 안에 말을 시작하지 않으면 녹음을 멈춰요. 봇이 다 읽어 주면 마이크가 저절로
							열리는 챗이라면, 학습자가 <b>대답을 떠올릴 시간</b>이에요. 길게 잡아도 요금은 늘지 않습니다.
						</p>
						<p class="tw-mb-1.5 tw-mt-3 tw-text-xs tw-font-medium tw-text-foreground">말이 끝났다고 볼 때까지</p>
						<Select
							value={chat.sttEndPauseMs ? String(chat.sttEndPauseMs) : ""}
							disabled={!isProPlan || !chat.isSttFlag()}
							on:change={(e) => chat.changeSttEndPauseMs(e.target.value)}
						>
							<option value="">기본 (1.5초)</option>
							<option value="700">0.7초 · 가장 빠른 응답</option>
							<option value="1200">1.2초</option>
							<option value="2000">2초</option>
							<option value="3000">3초</option>
							{#if chat.sttEndPauseMs && ![700, 1200, 2000, 3000].includes(chat.sttEndPauseMs)}
								<option value={String(chat.sttEndPauseMs)}>{chat.sttEndPauseMs / 1000}초</option>
							{/if}
						</Select>
						<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
							말하다가 이만큼 조용하면 다 말한 것으로 보고 받아쓰기로 넘겨요. 짧으면 <b>문장 중간에 쉬는 순간 잘리고</b>,
							길면 그만큼 <b>봇의 대답이 늦게</b> 시작돼요. 다 말한 학습자는 정지 버튼으로 바로 보낼 수 있어요.
						</p>
					</div>
					<!-- 음성 인식 언어. Whisper의 language는 힌트가 아니라 강제 지정이라, 영어 수업 챗을
					     한국어로 두면 영어 발화가 한글로 받아적힌다. 한/영을 섞어 말하는 어학 챗은 '자동 감지'. -->
					<div
						class="tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2.5"
						class:tw-opacity-60={!isProPlan || !chat.isSttFlag()}
					>
						<p class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">인식 언어</p>
						<Select
							value={chat.sttLanguage}
							disabled={!isProPlan || !chat.isSttFlag()}
							on:change={(e) => chat.changeSttLanguage(e.target.value)}
						>
							<option value="">한국어 (기본)</option>
							<option value="auto">자동 감지 (여러 언어를 섞어 말할 때)</option>
							<option value="en">영어</option>
							<option value="en-only">영어만 (한국어로 말하면 다시 요청)</option>
							<option value="ja">일본어</option>
							<option value="zh">중국어</option>
							<option value="es">스페인어</option>
							<option value="fr">프랑스어</option>
						</Select>
						<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
							사용자가 말한 음성을 어떤 언어로 받아쓸지예요. 영어 회화 수업이라면 <b>영어</b>로 두세요.
							한국어로 질문도 받는 수업이면 <b>자동 감지</b>가 낫습니다(짧은 대답은 가끔 틀릴 수 있어요).
							<br />
							<b>영어만</b>은 한 단계 더 강합니다 — 한국어로 말했거나 잡음이 한글로 잘못 인식되면 그
							결과를 <b>입력하지 않고</b> "영어로 말해 주세요" 안내를 띄워요(영어로 번역해서 넣지
							않습니다. 말하기 연습인데 학습자가 하지 않은 말이 전송되면 안 되니까요).
						</p>
					</div>
					<!-- 받아쓰기 모델. whisper-1 과 gpt-4o-transcribe 는 분당 단가가 같고, mini 는 절반이다. -->
					<div
						class="tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2.5"
						class:tw-opacity-60={!isProPlan || !chat.isSttFlag()}
					>
						<p class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">인식 모델</p>
						<Select
							value={chat.sttModel}
							disabled={!isProPlan || !chat.isSttFlag()}
							on:change={(e) => chat.changeSttModel(e.target.value)}
						>
							<option value="">기본 (Whisper)</option>
							<option value="gpt-4o-transcribe">GPT-4o Transcribe · 정확도 우선</option>
							<option value="gpt-4o-mini-transcribe">GPT-4o mini Transcribe · 가장 저렴</option>
						</Select>
						<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
							음성을 텍스트로 바꾸는 엔진이에요. 다국어를 섞어 쓰거나 짧은 대답이 많으면
							<b>GPT-4o Transcribe</b>가 더 정확해요(기본과 같은 요금).
						</p>
					</div>
					{#if !isProPlan}
						<button
							type="button"
							class="tw-text-xs tw-font-medium tw-text-primary hover:tw-underline"
							on:click={openPlanPopup}
						>PRO 요금제로 업그레이드하고 음성 채팅 켜기 →</button>
					{/if}
				</div>
			</Section>
		{/if}

		<!-- 이미지 -->
		<Section title="이미지를 적용해주세요!">
			<div class="tw-grid tw-grid-cols-2 tw-gap-4">
				{#if !isCreepong}
					<!-- 이 영역을 만지는 동안 미리보기 헤더의 로고 자리를 깜빡여 어디가 바뀌는지 알려준다 -->
					<div
						role="group"
						on:mouseenter={() => highlightArea("logo")}
						on:mouseleave={clearHighlight}
						on:focusin={() => highlightArea("logo")}
						on:focusout={clearHighlight}
					>
						<div class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">상단 로고</div>
						<ImageUploadCard
							pickMode
							imageUrl={chat.getLogoImageContent()}
							hint="배경이 투명한 png"
							emptyText="클릭해서 이미지 자산에서 고르세요"
							on:pick={() => (logoPickerOpen = true)}
							on:remove={onLogoRemove}
						/>
						{#if chat.chatVersion === "V3"}
							<div class="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-border tw-bg-muted/40 tw-px-4 tw-py-3">
								<span class="tw-text-sm tw-text-foreground">로고 옆에 챗 이름 표시</span>
								<Switch checked={showTitle} on:click={toggleShowTitle} />
							</div>
						{/if}
					</div>
				{/if}
				<div>
					<div class="tw-mb-1.5 tw-text-sm tw-font-medium tw-text-foreground">하단 이미지</div>
					<ImageUploadCard
						imageUrl={chat.characterImageContent ? chat.getCharacterImageContent() : null}
						hint="배경이 투명한 png, gif"
						on:file={(e) => onImageFileUpload(e.detail, "character-images", "characterImageContent")}
						on:remove={() => chat.change("characterImageContent", null)}
					/>
				</div>
			</div>
		</Section>

		<!-- 학습 진행도 표기(V3 전용) -->
		{#if chat.chatVersion === "V3"}
			<Section title="학습 진행도 표기를 선택해주세요!">
				<Select value={progressFormat} on:change={onProgressFormatChange}>
					<option value="count">개수 — 3/5</option>
					<option value="percent">퍼센트 — 60%</option>
					<option value="both">둘 다 — 60% · 3/5</option>
				</Select>
				<p class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
					레슨·평가(코스)가 있는 챗에서 상단 <b>진행바</b>와 헤더 <b>진행도</b> 아이콘으로 여는 패널에 함께 적용돼요.
					진행도가 없는 챗이면 아무 데도 나타나지 않아요.
				</p>
			</Section>
		{/if}

		<!-- 채팅 테마 -->
		<Section title="채팅 테마를 선택해주세요!">
			<div class="theme-preset-wrap">
				<div class="theme-preset" class:selected={chat.getThemePreset() === "light"} on:click={() => chat.changeThemePreset("light")}>
					<div class="theme-preset-preview light">
						<span class="pv-bubble profile" />
						<span class="pv-bubble user" />
					</div>
					<div class="theme-preset-name">밝은 버전</div>
				</div>
				<div class="theme-preset" class:selected={chat.getThemePreset() === "dark"} on:click={() => chat.changeThemePreset("dark")}>
					<div class="theme-preset-preview dark">
						<span class="pv-bubble profile" />
						<span class="pv-bubble user" />
					</div>
					<div class="theme-preset-name">어두운 버전</div>
				</div>
				<div class="theme-preset" class:selected={chat.getThemePreset() === "custom"} on:click={() => chat.changeThemePreset("custom")}>
					<div class="theme-preset-preview custom">
						<span class="pv-bubble profile" />
						<span class="pv-bubble user" />
					</div>
					<div class="theme-preset-name">직접 설정</div>
				</div>
			</div>
		</Section>

		{#if chat.isCustomTheme()}
			<Section title="채팅 버블의 색상을 지정해주세요!">
				<div class="tw-flex tw-flex-col tw-gap-3">
					<ChatBubbleColorPicker color={chat.profileChatCss.color} on:colorChange={(e) => chat.changeProfileChatBackColor(e.detail)} label="AI챗 채팅 버블" />
					<ChatBubbleColorPicker color={chat.userChatCss.color} on:colorChange={(e) => chat.changeUserChatBackColor(e.detail)} label="사용자 채팅버블 / 전송버튼" />
					{#if chat.chatVersion !== "V3"}
						<ChatBubbleColorPicker color={chat.fixedButtonCss.color} on:colorChange={(e) => chat.changeFixedButtonColor(e.detail)} label="하단 고정 버튼" />
					{/if}
				</div>
				<div class="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2">
					<span class="tw-text-sm tw-font-medium tw-text-foreground">어두운 배경 사용</span>
					<Switch checked={chat.isDarkMode()} on:click={() => chat.toggleDarkMode()} />
				</div>
			</Section>
		{/if}

		{#if chat.isCharacterChat()}
			<Section title="AI챗의 카테고리를 지정해주세요!">
				<div class="category-wrapper">
					{#each chat.categoryList || [] as category}
						<div class="category" data-no={category.id} on:click={clickCategory}>
							<div class="item" class:selected={chat.category === category.id}>
								{category.name}
							</div>
						</div>
					{/each}
				</div>
			</Section>
		{/if}

		<!-- 미니 페이지 링크는 블록 모드(V3)에선 숨김 — V3는 프로필 페이지 연동으로 대체 -->
		{#if !isCreepong && chat.chatVersion !== "V3"}
			<Section title="미니 페이지 링크를 연결해보세요!">
				<svelte:fragment slot="badge">
					<div class="link-tooltip-wrapper">
						<img src="/static/images/question-icon.png" class="link-question-icon" alt="도움말" />
						<div class="link-tooltip-bubble">AI챗 하단에 표시되는 외부 링크입니다. 미니 페이지나 소셜 링크 등 연결할 주소를 입력해주세요.</div>
					</div>
				</svelte:fragment>
				<input
					class={inputCls}
					maxlength="256"
					value={chat.linkUrl}
					on:change={(e) => chat.change("linkUrl", e.target.value)}
					placeholder="연결할 주소를 첨부해주세요!"
					type="text"
				/>
			</Section>
		{/if}
	</div>

	<ImagePicker
		bind:open={logoPickerOpen}
		chatId={chat.chatId}
		contextFolder={chatAssetFolder(chat)}
		mode="single"
		currentAssetId={chat.logoAssetId}
		on:select={onLogoPick}
	/>
{/if}

<style>
	.category-wrapper {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
	}
	.category {
		flex: 0 0 31.5%;
		box-sizing: border-box;
		height: 46px;
		display: flex;
		cursor: pointer;
	}
	.category .item {
		display: flex;
		justify-content: center;
		align-items: center;
		border-radius: 8px;
		width: 100%;
		height: 100%;
		background-color: hsl(var(--sb-muted));
		color: hsl(var(--sb-muted-foreground));
		font-size: 14px;
	}
	.category .item.selected {
		background-color: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
	}

	.link-tooltip-wrapper {
		position: relative;
		display: inline-flex;
		align-items: center;
	}
	.link-question-icon {
		width: 16px;
		height: 16px;
		cursor: pointer;
	}
	.link-tooltip-bubble {
		display: none;
		position: absolute;
		bottom: 24px;
		right: 0;
		background: hsl(var(--sb-foreground));
		color: hsl(var(--sb-background));
		font-size: 12px;
		padding: 6px 10px;
		border-radius: 6px;
		width: 240px;
		z-index: 10;
	}
	.link-tooltip-wrapper:hover .link-tooltip-bubble {
		display: block;
	}

	.fixed-button-list {
		list-style: none;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 10px;
		margin-bottom: 10px;
	}
	.fixed-button-item {
		display: flex;
		align-items: flex-start;
		gap: 8px;
	}
	.fixed-button-item .move-icon {
		margin-top: 14px;
		cursor: grab;
		flex-shrink: 0;
		opacity: 0.6;
	}

	/* ── V3 테마 프리셋 선택 (프리뷰 스와치는 챗 테마를 나타내므로 고정색 유지) ── */
	.theme-preset-wrap {
		display: flex;
		gap: 12px;
		flex-wrap: wrap;
	}
	.theme-preset {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		width: 96px;
		padding: 10px;
		border: 2px solid hsl(var(--sb-border));
		border-radius: 10px;
		cursor: pointer;
		transition: border-color 0.15s, box-shadow 0.15s;
	}
	.theme-preset:hover {
		border-color: hsl(var(--sb-muted-foreground) / 0.5);
	}
	.theme-preset.selected {
		border-color: hsl(var(--sb-primary));
		box-shadow: 0 0 0 1px hsl(var(--sb-primary)) inset;
	}
	.theme-preset-preview {
		width: 100%;
		height: 56px;
		border-radius: 8px;
		padding: 8px;
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: 5px;
	}
	.theme-preset-preview.light {
		background: #ffffff;
		border: 1px solid #e7e9ee;
	}
	.theme-preset-preview.dark {
		background: #171717;
		border: 1px solid #2f2f33;
	}
	.theme-preset-preview.custom {
		background: linear-gradient(135deg, #ffffff 0 50%, #171717 50% 100%);
		border: 1px solid #e7e9ee;
	}
	.pv-bubble {
		display: block;
		height: 12px;
		border-radius: 6px;
	}
	.pv-bubble.profile {
		width: 60%;
		align-self: flex-start;
	}
	.pv-bubble.user {
		width: 50%;
		align-self: flex-end;
	}
	.theme-preset-preview.light .pv-bubble.profile,
	.theme-preset-preview.custom .pv-bubble.profile {
		background: #f3f4f6;
		border: 1px solid #e5e7eb;
	}
	.theme-preset-preview.light .pv-bubble.user,
	.theme-preset-preview.custom .pv-bubble.user {
		background: #2563eb;
	}
	.theme-preset-preview.dark .pv-bubble.profile {
		background: #2f2f33;
	}
	.theme-preset-preview.dark .pv-bubble.user {
		background: #3b82f6;
	}
	.theme-preset-name {
		font-size: 13px;
		color: hsl(var(--sb-muted-foreground));
		white-space: nowrap;
	}
	.theme-preset.selected .theme-preset-name {
		color: hsl(var(--sb-primary));
		font-weight: 600;
	}

	.theme-dark-toggle {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 12px;
		font-size: 13px;
		color: hsl(var(--sb-muted-foreground));
		cursor: pointer;
	}
	.theme-dark-toggle input {
		width: 16px;
		height: 16px;
		cursor: pointer;
	}
</style>
